<script setup lang="ts" generic="T">
	import { Check, ChevronsUpDown, Loader2, X } from 'lucide-vue-next'
	import { computed, ref } from 'vue'

	import {
		Command,
		CommandEmpty,
		CommandGroup,
		CommandInput,
		CommandList,
	} from '@/components/ui/command'
	import {
		Popover,
		PopoverContent,
		PopoverTrigger,
	} from '@/components/ui/popover'
	import CommandItem from '@/components/ui/command/CommandItem.vue'
	import { cn } from '@/lib/utils'

	/**
	 * Selector con búsqueda, sobre Popover + Command.
	 *
	 * Existe para los campos cuyo catálogo es demasiado grande para un `select`
	 * —empresas, personas— y donde además la búsqueda ocurre en el servidor: el
	 * evento `search` sale en cada tecla y el consumidor decide qué pedir.
	 *
	 * El filtrado local de `Command` NO estorba: filtra por el `textContent` de
	 * cada opción, así que si el slot pinta nombre, correo y empresa, cualquier
	 * resultado que haya devuelto el servidor por esos campos sigue visible.
	 */
	interface Props {
		modelValue?: T
		options: T[]
		/** Texto de la opción ya elegida, en el botón. */
		displayValue: (option: T) => string
		/** Clave estable; sin ella dos opciones con el mismo texto se pisan. */
		optionKey: (option: T) => string | number
		placeholder?: string
		searchPlaceholder?: string
		emptyText?: string
		loading?: boolean
		disabled?: boolean
		clearable?: boolean
		id?: string
		describedBy?: string
	}

	const props = withDefaults(defineProps<Props>(), {
		placeholder: 'Seleccione una opción',
		searchPlaceholder: 'Buscar…',
		emptyText: 'Sin resultados',
		clearable: true,
	})

	const emit = defineEmits<{
		(e: 'update:modelValue', value: T | undefined): void
		(e: 'search', query: string): void
	}>()

	const open = ref(false)

	const selectedLabel = computed(() =>
		props.modelValue !== undefined && props.modelValue !== null ?
			props.displayValue(props.modelValue as T)
		:	''
	)

	const isSelected = (option: T) =>
		props.modelValue !== undefined &&
		props.modelValue !== null &&
		props.optionKey(option) === props.optionKey(props.modelValue as T)

	const pick = (option: T) => {
		emit('update:modelValue', option)
		open.value = false
	}

	const clear = () => {
		emit('update:modelValue', undefined)
	}

	const onSearch = (event: Event) => {
		emit('search', (event.target as HTMLInputElement).value)
	}
</script>

<template>
	<Popover v-model:open="open">
		<div class="relative">
			<PopoverTrigger as-child>
				<button
					:id="id"
					type="button"
					role="combobox"
					:aria-expanded="open"
					:aria-describedby="describedBy"
					:disabled="disabled"
					:class="
						cn(
							'flex h-9 w-full items-center justify-between gap-2 rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm transition-colors',
							'focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
							selectedLabel ? 'text-foreground' : 'text-muted-foreground',
							clearable && selectedLabel ? 'pr-14' : ''
						)
					"
				>
					<span class="truncate text-start">
						{{ selectedLabel || placeholder }}
					</span>
					<ChevronsUpDown class="h-4 w-4 shrink-0 opacity-50" />
				</button>
			</PopoverTrigger>

			<!-- Fuera del trigger: dentro, el clic abriría el popover en vez de
			     limpiar. -->
			<button
				v-if="clearable && selectedLabel && !disabled"
				type="button"
				class="absolute right-8 top-1/2 -translate-y-1/2 rounded p-0.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
				:aria-label="`Quitar ${selectedLabel}`"
				@click="clear"
			>
				<X class="h-3.5 w-3.5" />
			</button>
		</div>

		<!-- `side="bottom"` es el default de reka-ui, pero con `avoidCollisions`
		     se voltea hacia arriba si no cabe abajo. Se deja así a propósito: es
		     preferible a que la lista se salga de la pantalla. `collisionPadding`
		     evita que quede pegada al borde cuando sí abre hacia abajo. -->
		<PopoverContent
			class="w-[var(--reka-popover-trigger-width)] min-w-[15rem] p-0"
			align="start"
			:collision-padding="8"
		>
			<Command>
				<CommandInput
					:placeholder="searchPlaceholder"
					@input="onSearch"
				/>

				<CommandList>
					<div
						v-if="loading"
						class="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground"
					>
						<Loader2 class="h-4 w-4 animate-spin" />
						Buscando…
					</div>

					<template v-else>
						<CommandEmpty>{{ emptyText }}</CommandEmpty>

						<div
							v-if="!options.length"
							class="py-6 text-center text-sm text-muted-foreground"
						>
							{{ emptyText }}
						</div>

						<CommandGroup v-else>
							<CommandItem
								v-for="option in options"
								:key="optionKey(option)"
								:value="String(optionKey(option))"
								class="cursor-pointer items-start"
								@select="pick(option)"
							>
								<Check
									:class="
										cn(
											'mt-0.5 h-4 w-4 shrink-0',
											isSelected(option) ? 'opacity-100' : 'opacity-0'
										)
									"
								/>
								<span class="min-w-0 flex-1">
									<slot
										name="option"
										:option="option"
									>
										{{ displayValue(option) }}
									</slot>
								</span>
							</CommandItem>
						</CommandGroup>
					</template>
				</CommandList>
			</Command>
		</PopoverContent>
	</Popover>
</template>
